import { useState, useEffect, useRef } from 'react';
import { pipeline } from '@xenova/transformers';

interface Translation {
  language: string;
  code: string;
  text: string;
}

export default function LiveTranslator() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [translations, setTranslations] = useState<Translation[]>([]);
  const availableLanguages = [
    { code: 'de', name: 'German' },
    { code: 'fr', name: 'French' },
    { code: 'es', name: 'Spanish' },
    { code: 'it', name: 'Italian' },
    { code: 'pt', name: 'Portuguese' },
  ];
  const [targetLanguages, setTargetLanguages] = useState<Array<{code:string;name:string}>>([]);

  const [modelLoaded, setModelLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isTranslating, setIsTranslating] = useState(false);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const transformerRef = useRef<any>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout>();

  // Initialize speech recognition
  useEffect(() => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Browser does not support speech recognition.');
      return;
    }
    const SpeechRecognition = window['webkitSpeechRecognition'] || window['SpeechRecognition'];
    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsRecording(true);
      setError(null);
      setTranscription('');
      setTranslations([]);
    };
    recognition.onend = () => setIsRecording(false);
    recognition.onerror = (event) => {
      setError(`Speech recognition error: ${event.error}`);
      setIsRecording(false);
    };
    recognition.onresult = (event) => {
      let finalTranscript = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i][0].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      if (finalTranscript) {
        setIsTranslating(true);
        clearTimeout(debounceTimerRef.current as NodeJS.Timeout);
debounceTimerRef.current = setTimeout(() => {
          setTranscription(finalTranscript);
          // Save to history
          const newHistory = [finalTranscript, ...history];
          setHistory(newHistory);
          localStorage.setItem('translator_history', JSON.stringify(newHistory));
          translateText(finalTranscript);
          setIsTranslating(false);
        }, 25000); // 25 sec pause detection
      }
    };
    return () => {
      if (recognitionRef.current) recognitionRef.current.stop();
    };
  }, []);

  // Load translation model once
  useEffect(() => {
    // Pre‑load individual language pipelines (lighter models) when possible.
    const loadPipelines = async () => {
      try {
        const pipelines: Record<string, any> = {};
        // German pipeline
        pipelines['de'] = await pipeline('translation', 'Helsinki-NLP/opus-mt-en-de');
        // French pipeline
        pipelines['fr'] = await pipeline('translation', 'Helsinki-NLP/opus-mt-en-fr');
        // Add more languages here if you want (e.g., 'es', 'it', ...)
        transformerRef.current = pipelines;
        setModelLoaded(true);
      } catch (e) {
        setError('Failed to load translation pipelines.');
        console.error(e);
      }
    };
    loadPipelines();
  }, []);

  // Auto‑start listening when model is ready and driver schedule is set
  useEffect(() => {
    if (modelLoaded && targetLanguages.length > 0) {
      // Load driver schedule languages from localStorage (if any)
      const saved = JSON.parse(localStorage.getItem('driver_schedule') ?? '[]');
      if (Array.isArray(saved) && saved.length) setTargetLanguages(saved);
      // Start recognition automatically
      if (recognitionRef.current) recognitionRef.current.start();
    }
  }, [modelLoaded, targetLanguages]);

  const translateText = async (text: string) => {
    if (!modelLoaded) return;
    try {
      const results = await Promise.all(
        targetLanguages.map(async (lang) => {
          const pipeline = transformerRef.current[lang.code];
          if (!pipeline) throw new Error(`No pipeline for ${lang.code}`);
          const output = await pipeline(text, {
            padding: 'max_length',
            truncation: true,
          });
          return { language: lang.name, code: lang.code, text: output } as Translation;
        })
      );
      setTranslations(results);
    } catch (e) {
      console.error('Translation error', e);
    }
  };

  const toggleRecording = () => {
    if (!recognitionRef.current) return;
    if (isRecording) recognitionRef.current.stop();
    else recognitionRef.current.start();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
        <div className="p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Live Translator</h1>
          {error && <div className="bg-red-50 border border-red-400 text-red-700 p-3 rounded mb-4">{error}</div>}
          {!modelLoaded && <div className="bg-yellow-50 border border-yellow-400 text-yellow-700 p-3 rounded mb-4">Loading model…</div>}
          {isRecording ? (
            <p className="text-sm text-gray-600 mb-2">Listening…</p>
          ) : (
            <p className="text-sm text-gray-600 mb-2">Initializing...</p>
          )}
          {isTranslating && <p className="text-sm text-gray-600 mb-2">Processing translation…</p>}
          {transcription && (
            <div className="mb-4">
              <h2 className="font-semibold">Original (English)</h2>
              <p className="bg-gray-100 p-2 rounded">{transcription}</p>
            </div>
          )}
          {translations.length > 0 && (
            <div>
              <h2 className="font-semibold mb-2">Translations</h2>
              {translations.map((t, i) => (
                <div key={i} className="border p-2 rounded mb-2">
                  <div className="flex justify-between items-center">
                    <span>{t.language}</span>
                    <button
                      onClick={() => {
                        const utter = new SpeechSynthesisUtterance(t.text);
                        utter.lang = t.code === 'de' ? 'de-DE' : 'fr-FR';
                        utter.rate = 0.9;
                        window.speechSynthesis.speak(utter);
                      }}
                      className="text-blue-600"
                    >
                      🔊
                    </button>
                  </div>
                  <p className="mt-1">{t.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
